import { useEffect, useState } from "react";
import useInjectScript from "../Hooks/useInjectScript";

/* global google */

const defaultConfiguration = {
  clientId: "",
  developerKey: "",
  viewId: "DOCS",
  callbackFunction: () => null,
};

const SCOPES = ["https://www.googleapis.com/auth/drive.file"];

export default function useDrivePicker() {
  const [loadedApi, error] = useInjectScript("https://apis.google.com/js/api.js");
  const [loadedGsi, errorGsi] = useInjectScript("https://accounts.google.com/gsi/client");
  const [pickerApiLoaded, setPickerApiLoaded] = useState(false);
  const [openAfterAuth, setOpenAfterAuth] = useState(false);
  const [config, setConfig] = useState(defaultConfiguration);
  const [authRes, setAuthRes] = useState();

  let picker;

  const onPickerApiLoad = () => {
    setPickerApiLoaded(true);
  };

  // load the Drive picker api
  const loadApis = () => {
    window.gapi.load("auth");
    window.gapi.load("picker", { callback: onPickerApiLoad });
  };

  const createPicker = ({
    token,
    appId = "",
    supportDrives = false,
    developerKey,
    viewId = "DOCS",
    disabled,
    multiselect,
    setOrigin,
    showUploadView = false,
    showUploadFolders,
    setParentFolder = "",
    viewMimeTypes,
    customViews,
    locale = "en",
    setIncludeFolders,
    setSelectFolderEnabled,
    disableDefaultView = false,
    callbackFunction,
    onPickerShow,
  }) => {
    if (disabled) {
      return false;
    }

    const view = new google.picker.DocsView(google.picker.ViewId[viewId]);
    if (viewMimeTypes) view.setMimeTypes(viewMimeTypes);
    if (setIncludeFolders) view.setIncludeFolders(true);
    if (setSelectFolderEnabled) view.setSelectFolderEnabled(true);

    const uploadView = new google.picker.DocsUploadView();
    if (viewMimeTypes) uploadView.setMimeTypes(viewMimeTypes);
    if (showUploadFolders) uploadView.setIncludeFolders(true);
    if (setParentFolder) uploadView.setParent(setParentFolder);
    if (setParentFolder) view.setParent(setParentFolder);

    picker = new google.picker.PickerBuilder()
      .setAppId(appId)
      .setOAuthToken(token)
      .setLocale(locale)
      .setCallback(callbackFunction)
      .enableFeature(google.picker.Feature.NAV_HIDDEN);

    if (developerKey) {
      picker.setDeveloperKey(developerKey);
    }

    if (setOrigin) {
      picker.setOrigin(setOrigin);
    }

    if (!disableDefaultView) {
      picker.addView(view);
    }

    if (customViews) {
      customViews.map((view) => picker.addView(view));
    }

    if (multiselect) {
      picker.enableFeature(google.picker.Feature.MULTISELECT_ENABLED);
    }

    if (showUploadView) picker.addView(uploadView);

    if (supportDrives) {
      picker.enableFeature(google.picker.Feature.SUPPORT_DRIVES);
    }

    picker.build().setVisible(true);
    if (typeof onPickerShow === "function") {
      onPickerShow();
    }
    return true;
  };

  // get the apis from googleapis
  useEffect(() => {
    if (loadedApi && !error && loadedGsi && !errorGsi && !pickerApiLoaded) {
      loadApis();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedApi, error, loadedGsi, errorGsi, pickerApiLoaded]);

  // use effect to open picker after auth
  useEffect(
    () => {
      if (openAfterAuth && config.token && loadedApi && !error && loadedGsi && !errorGsi && pickerApiLoaded) {
        createPicker(config);
        setOpenAfterAuth(false);
      }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [openAfterAuth, config.token, loadedApi, error, loadedGsi, errorGsi, pickerApiLoaded]
  );

  // open the picker
  const openPicker = (config) => {
    // global scope given conf
    setConfig(config);

    // if we didnt get token generate token.
    if (!config.token) {
      const client = google.accounts.oauth2.initTokenClient({
        client_id: config.clientId,
        scope: (config.customScopes ? [...SCOPES, ...config.customScopes] : SCOPES).join(" "),
        callback: (tokenResponse) => {
          if (typeof tokenResponse.error !== "undefined" && typeof config.errorHandler === "function") {
            config.errorHandler(tokenResponse);
            setAuthRes(tokenResponse);
            return;
          }
          setAuthRes(tokenResponse);
          createPicker({ ...config, token: tokenResponse.access_token });
        },
      });
      client.requestAccessToken();
    }

    // if we have token and everything is loaded open the picker
    if (config.token && loadedApi && !error && pickerApiLoaded) {
      return createPicker(config);
    }
  };

  const isReady = loadedApi && !error && pickerApiLoaded && loadedGsi && !errorGsi;
  return [openPicker, authRes, isReady];
}
